import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC, s_PB } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { MOOD_OPTIONS } from '../../shared/constants';
import Sparkline from '../../components/ui/Sparkline';
import SleepMoodCheckinModal from '../../modals/SleepMoodCheckinModal';

function SleepMoodWeekCard({ lang }) {
  const nl = lang === 'nl';
  const [sleepLog, , reloadSleep] = useStorage('sl_sleep_log', []);
  const [moodLog,  , reloadMood]  = useStorage('sl_mood_log',  []);
  const [modalDate, setModalDate] = useState(null); // YYYY-MM-DD or null

  // Build last 7 days Mon→Sun (or current week ending today)
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });

  const todayKey   = new Date().toISOString().slice(0, 10);
  const todayDow   = (new Date().getDay() + 6) % 7; // 0=Mon
  const dayLabels  = nl ? ['Ma','Di','Wo','Do','Vr','Za','Zo'] : ['Mo','Tu','We','Th','Fr','Sa','Su'];

  const sleepPoints = days.map(d => { const e = sleepLog.find(s => s.date === d); return e ? (e.score ?? null) : null; });
  const moodPoints  = days.map(d => { const e = moodLog.find(m  => m.date === d); return e ? (e.score ?? null) : null; });

  const hasAnySleep = sleepPoints.some(v => v !== null);
  const hasAnyMood  = moodPoints.some(v => v !== null);
  const hasAnyData  = hasAnySleep || hasAnyMood;

  const fill = pts => {
    let last = pts.find(v => v !== null) ?? 5;
    return pts.map(v => { if (v !== null) { last = v; return v; } return last; });
  };

  const todaySleep     = sleepLog.find(s => s.date === todayKey);
  const todayMood      = moodLog.find(m  => m.date === todayKey);
  const todayMoodOpt   = MOOD_OPTIONS.find(m => m.id === todayMood?.mood);
  const missingToday   = !todaySleep || !todayMood;

  const trend = pts => {
    const valid = pts.filter(v => v !== null);
    if (valid.length < 2) return 0;
    const mid  = Math.floor(valid.length / 2);
    const avg1 = valid.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
    const avg2 = valid.slice(mid).reduce((a, b) => a + b, 0) / (valid.length - mid);
    return Math.round((avg2 - avg1) * 10) / 10;
  };
  const sleepTrend = trend(sleepPoints);
  const moodTrend  = trend(moodPoints);

  // Weekly averages
  const avgSleep = hasAnySleep ? Math.round(sleepPoints.filter(v=>v!==null).reduce((a,b)=>a+b,0) / sleepPoints.filter(v=>v!==null).length * 10) / 10 : null;
  const avgMood  = hasAnyMood  ? Math.round(moodPoints.filter(v=>v!==null).reduce((a,b)=>a+b,0)  / moodPoints.filter(v=>v!==null).length  * 10) / 10 : null;

  const handleSaved = () => {
    // Force re-read from localStorage by triggering a state update
    try {
      const s = JSON.parse(localStorage.getItem('sl_sleep_log') || '[]');
      const m = JSON.parse(localStorage.getItem('sl_mood_log')  || '[]');
      reloadSleep?.(s);
      reloadMood?.(m);
    } catch {}
    setModalDate(null);
  };

  return (
    <>
      {modalDate && (
        <SleepMoodCheckinModal
          lang={lang}
          date={modalDate}
          onClose={() => setModalDate(null)}
          onSaved={handleSaved}
        />
      )}

      <div style={s_GC({ marginBottom: 14 })}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontSize:13, color:_T.accent, letterSpacing:3, fontWeight:600 }}>
            😴 {nl ? 'SLAAP & STEMMING' : 'SLEEP & MOOD'}
          </div>
          {hasAnyData && (
            <button onClick={() => setModalDate(todayKey)}
              style={{ fontSize:10, padding:'4px 10px', borderRadius:8, cursor:'pointer', background:`${_T.accent}18`, border:`1px solid ${_T.accent}44`, color:_T.accent, fontWeight:600 }}>
              {nl?'+ Vandaag':'+ Today'}
            </button>
          )}
        </div>

        {/* Empty state */}
        {!hasAnyData && (
          <div style={{ textAlign:'center', padding:'12px 0 8px' }}>
            <div style={{ color:C.silver, fontSize:13, lineHeight:1.7, marginBottom:10 }}>
              {nl ? 'Tap een dag om slaap & stemming te loggen 👇' : 'Tap a day to log your sleep & mood 👇'}
            </div>
          </div>
        )}

        {/* Today missing banner */}
        {hasAnyData && missingToday && (
          <button onClick={() => setModalDate(todayKey)} style={{
            width:'100%', fontSize:11, padding:'7px 12px', marginBottom:10, cursor:'pointer',
            background:`${_T.accent}14`, border:`1px solid ${_T.accent}44`,
            borderRadius:10, color:_T.accent, fontWeight:600,
          }}>
            {nl ? '📝 Vandaag nog niet gelogd — invullen?' : '📝 Today not logged yet — fill in?'}
          </button>
        )}

        {/* Sleep + Mood summary cards */}
        {hasAnyData && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            {hasAnySleep && (
              <div style={{ background:_T.bg4, borderRadius:14, padding:'12px 14px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                  <span style={{ fontSize:16 }}>😴</span>
                  <span style={{ fontSize:12, color:C.teal, fontWeight:600 }}>{nl?'Slaap':'Sleep'}</span>
                  {sleepTrend!==0 && <span style={{ marginLeft:'auto', fontSize:11, color:sleepTrend>0?C.teal:C.orange, fontWeight:700 }}>{sleepTrend>0?'↑':'↓'} {Math.abs(sleepTrend)}</span>}
                </div>
                <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:22, color:C.white, fontWeight:700, lineHeight:1 }}>{todaySleep ? `${todaySleep.score}/10` : avgSleep ? `~${avgSleep}` : '–'}</div>
                    <div style={{ fontSize:9, color:C.silver, marginTop:2 }}>
                      {todaySleep
                        ? (todaySleep.hours != null ? `${todaySleep.hours}h · ${'⭐'.repeat(todaySleep.quality||0)}` : `${Math.floor((todaySleep.durationMins||0)/60)}h`)
                        : (nl?'gem. deze week':'avg this week')}
                    </div>
                  </div>
                  <Sparkline points={fill(sleepPoints)} color={C.teal} width={52} height={26}/>
                </div>
              </div>
            )}
            {hasAnyMood && (
              <div style={{ background:_T.bg4, borderRadius:14, padding:'12px 14px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                  <span style={{ fontSize:16 }}>💭</span>
                  <span style={{ fontSize:12, color:C.purple, fontWeight:600 }}>{nl?'Stemming':'Mood'}</span>
                  {moodTrend!==0 && <span style={{ marginLeft:'auto', fontSize:11, color:moodTrend>0?C.teal:C.orange, fontWeight:700 }}>{moodTrend>0?'↑':'↓'} {Math.abs(moodTrend)}</span>}
                </div>
                <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ fontSize:26, lineHeight:1 }}>{todayMoodOpt?.emoji ?? '–'}</div>
                    <div style={{ fontSize:9, color:C.silver, marginTop:2 }}>
                      {todayMoodOpt ? (nl?todayMoodOpt.label:todayMoodOpt.labelEN) : (nl?'gem. deze week':'avg this week')}
                    </div>
                  </div>
                  <Sparkline points={fill(moodPoints)} color={C.purple} width={52} height={26}/>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Interactive week bar ── */}
        <div style={{ display:'flex', justifyContent:'space-between', gap:4 }}>
          {days.map((d, i) => {
            const s       = sleepLog.find(e => e.date === d);
            const m       = moodLog.find(e  => e.date === d);
            const isToday = d === todayKey;
            const isFuture = d > todayKey;
            const hasBoth  = !!s && !!m;
            const moodOpt  = m ? MOOD_OPTIONS.find(o => o.id === m.mood) : null;
            const sleepCol = s ? (s.score >= 7 ? C.teal : s.score >= 5 ? _T.accent : C.red) : `${C.teal}22`;
            const moodCol  = moodOpt ? moodOpt.color : `${C.purple}22`;

            return (
              <button key={d}
                disabled={isFuture}
                onClick={() => !isFuture && setModalDate(d)}
                style={{
                  flex:1, background:isToday?`${_T.accent}10`:hasBoth?`${_T.bg4}`:_T.bg3,
                  border:`1px solid ${isToday?_T.accent+'55':_T.cardBorderBot}`,
                  borderRadius:10, padding:'6px 3px', cursor:isFuture?'default':'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                  opacity:isFuture?0.3:1, transition:'background 0.15s',
                }}>
                {/* Sleep dot */}
                <div style={{ width:8, height:8, borderRadius:'50%', background:sleepCol, border:isToday?`1.5px solid ${C.teal}`:'none' }}/>
                {/* Mood dot */}
                <div style={{ width:8, height:8, borderRadius:'50%', background:moodCol, border:isToday?`1.5px solid ${C.purple}`:'none' }}/>
                {/* Day label */}
                <span style={{ fontSize:8, color:isToday?_T.accent:C.silver, fontWeight:isToday?700:400 }}>{dayLabels[i]}</span>
                {/* Score or + */}
                {s && <span style={{ fontSize:7, color:C.teal }}>{s.score}</span>}
                {!s && !isFuture && <span style={{ fontSize:9, color:`${C.silver}55` }}>+</span>}
              </button>
            );
          })}
        </div>

        {/* Weekly averages footer */}
        {hasAnyData && (
          <div style={{ display:'flex', gap:12, marginTop:10, paddingTop:10, borderTop:`1px solid ${_T.cardBorderBot}` }}>
            {avgSleep!==null && <span style={{ fontSize:10, color:C.teal }}>😴 {nl?'gem.':'avg'} {avgSleep}/10</span>}
            {avgMood!==null  && <span style={{ fontSize:10, color:C.purple }}>💭 {nl?'gem.':'avg'} {avgMood}/5</span>}
            <span style={{ fontSize:10, color:C.silver, marginLeft:'auto' }}>
              {sleepPoints.filter(v=>v!==null).length}/{days.length} {nl?'dagen':'days'}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

export default SleepMoodWeekCard;
